<template>
  <TransferStepCard :step="2" :title="title" @back="$emit('back')">
    <div class="flex flex-col gap-4">
      <div class="flex flex-col lg:flex-row gap-3">
        <AppInput
          class="w-full lg:flex-1"
          :model-value="search"
          :placeholder="searchPlaceholder"
          @update:model-value="$emit('update:search', String($event ?? ''))"
        >
          <template #icon>
            <Search :size="16" class="text-(--p-primary-color) dark:stroke-white" />
          </template>
        </AppInput>

        <AppSelect
          class="w-full lg:w-[250px] lg:flex-none"
          :model-value="selectedCampaignId"
          :options="campaignOptions"
          :placeholder="campaignPlaceholder"
          :show-clear="true"
          @update:model-value="handleCampaignChange"
          :editable="false"
        >
          <template #icon>
            <IconCampaign
              width="16"
              height="16"
              :stroke-width="1.5"
              class="text-(--p-primary-color) dark:text-white"
            />
          </template>
        </AppSelect>
      </div>

      <div v-if="loading" class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <div
          v-for="index in 6"
          :key="index"
          class="app-border-card-calss animate-pulse rounded-2xl p-4 shadow-sm backdrop-blur-md backdrop-saturate-125 dark:bg-[color-mix(in_srgb,var(--p-surface-900)_42%,transparent)]"
        >
          <div class="flex items-center gap-3">
            <div
              class="h-12 w-12 rounded-full border border-[var-(--p-primary-50)]/50 bg-[color-mix(in_srgb,var(--p-primary-color)_10%,transparent)] shadow-sm dark:bg-[color-mix(in_srgb,var(--p-primary-color)_16%,var(--p-surface-900))]"
            />
            <div class="flex-1 space-y-2">
              <div
                class="h-4 rounded bg-[color-mix(in_srgb,var(--p-surface-400)_28%,transparent)] dark:bg-[color-mix(in_srgb,var(--p-surface-500)_24%,transparent)]"
              />
              <div
                class="h-3 w-2/3 rounded bg-[color-mix(in_srgb,var(--p-surface-400)_20%,transparent)] dark:bg-[color-mix(in_srgb,var(--p-surface-500)_18%,transparent)]"
              />
            </div>
          </div>
        </div>
      </div>

      <div
        v-else-if="users.length"
        class="grid max-h-[calc((4rem*4)+(1rem*2))] grid-cols-1 lg:grid-cols-3 gap-4 overflow-y-auto pr-2 pb-2"
      >
        <button
          v-for="user in users"
          :key="user.id"
          type="button"
          class="app-card-glass shadow-sm dark:bg-transparent text-left cursor-pointer rounded-2xl border p-4 transition-all duration-200"
          :class="
            modelValue?.id === user.id
              ? 'border-[var(--p-primary-color)] bg-[var(--p-primary-color)]/10 shadow-md'
              : 'hover:bg-[var(--p-primary-50)]/50 dark:hover:bg-[var(--p-primary-800)]/10  hover:shadow-md'
          "
          @click="$emit('update:modelValue', user)"
        >
          <div class="flex items-center gap-3">
            <AppAvatar
              :image="user.avatar || null"
              :label="getInitials(user.name)"
              size="normal"
              class="shadow-md shadow-[var(--p-primary-color)] text-sm! font-semibold dark:bg-[var(--p-primary-color)]/20! [&_img]:h-full [&_img]:w-full [&_img]:object-cover [&_img]:object-center"
            />
            <div class="min-w-0 flex-1">
              <p
                class="font-semibold text-sm truncate text-[var(--p-primary-color)] dark:text-[var(--p-text-color)]"
              >
                {{ user.name }}
              </p>
              <p
                v-if="user.alias"
                class="text-xs text-[var(--p-surface-500)] dark:text-[var(--p-surface-100)] truncate"
              >
                {{ user.alias }}
              </p>
              <div class="flex flex-row gap-1">
                <IconCampaign
                  width="13"
                  height="16"
                  class="text-[var(--p-primary-color)] dark:text-[var(--p-surface-100)]"
                />
                <p
                  class="text-xs text-[var(--p-surface-700)] dark:text-[var(--p-surface-100)] truncate"
                >
                  {{ user.campaignName }}
                </p>
              </div>
            </div>
          </div>
        </button>
      </div>

      <div
        v-else
        class="rounded-2xl border border-dashed! border-[var(--p-surface-300)]! dark:border-[var(--p-surface-700)] p-6 text-center text-sm text-[var(--p-surface-500)] dark:text-[var(--p-surface-400)]"
      >
        {{ emptyText }}
      </div>
    </div>
  </TransferStepCard>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import AppAvatar from '@/components/atoms/avatar/AppAvatar.vue'
import AppInput from '@/components/atoms/inputs/AppInput.vue'
import AppSelect from '@/components/atoms/selects/AppSelect.vue'
import type { SelectOption } from '@/components/atoms/selects/types/select-option.types'
import TransferStepCard from './TransferStepCard.vue'
import type { CampaignOption } from '@/components/whatsapp/composables/useTransferDialog'
import IconCampaign from '@/assets/svg/campaign_icon.svg?component'
import { Search } from 'lucide-vue-next'

export interface TransferUserCardOption {
  id: string
  name: string
  email?: string
  avatar?: string | null
  alias?: string | null
  campaignId: string
  campaignName: string
}

interface Props {
  modelValue: TransferUserCardOption | null
  users: TransferUserCardOption[]
  campaigns: CampaignOption[]
  selectedCampaign: CampaignOption | null
  search: string
  loading: boolean
  title: string
  searchPlaceholder: string
  campaignPlaceholder: string
  emptyText: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'update:modelValue': [value: TransferUserCardOption | null]
  'update:selectedCampaign': [value: CampaignOption | null]
  'update:search': [value: string]
  back: []
}>()

const campaignOptions = computed<SelectOption[]>(() =>
  props.campaigns.map((campaign) => ({
    name: campaign.name,
    value: campaign.id,
  })),
)

const selectedCampaignId = computed(() => props.selectedCampaign?.id ?? null)

const handleCampaignChange = (campaignId: string | number | boolean | null) => {
  const normalizedCampaignId = typeof campaignId === 'boolean' ? null : campaignId

  const nextCampaign =
    normalizedCampaignId === null
      ? null
      : (props.campaigns.find((campaign) => campaign.id === String(normalizedCampaignId)) ?? null)

  emit('update:selectedCampaign', nextCampaign)
}

const getInitials = (name: string) => {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('')
}
</script>
